import * as React from 'react';

import PropTypes from 'prop-types';

import Error from './components/Error';
import Install from './components/Install';
import Installing from './components/Installing';
import Print from './components/Print';
import Printing from './components/Printing';
import {
  getDefaultPrinter,
  getLabelPrinterModel,
  getLocalPrinters,
  getPrinterStatus,
} from './utils/printer';

/**
 * List of modal steps
 */
const steps = {
  ERROR: 'Error',
  INSTALL: 'Install',
  INSTALLING: 'Installing',
  PRINT: 'Print',
  PRINTING: 'Printing',
};

const links = {
  documentation:
    'https://retail-support.lightspeedhq.com/hc/en-us/articles/115000084827-Setting-up-the-Zebra-ZD410-on-a-computer-with-Zebra-Browser-Print',
  download: 'https://downloads.lightspeedretailapp.com/',
};

/**
 * List of modal components
 */
const Components = {
  Error,
  Install,
  Installing,
  Print,
  Printing,
};

/**
 * Timeouts in MS
 */
const timeouts = {
  getPrintersInitial: 2000,
  getPrinters: 5000,
  getPrinterStatus: 2000,
  appInstalledRedirect: 2000,
};

/**
 * Class LabelPrintModal builds and handle the label printing steps when the action is requested.
 * The "Enable Zebra Browser Print and Lightspeed Hub" checkbox needs to be enabled inside the shop options
 */
class LabelPrintModal extends React.Component {
  /**
   * Get the list of modal steps
   * @returns {Object}
   */
  static get steps() {
    return steps;
  }

  /**
   * Get the list of links
   * @returns {Object}
   */
  static get links() {
    return links;
  }

  /**
   * Construct the ZBP Modal
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.state = {
      show: true,
      isInstalled: false,
      printer: {},
      printers: [],
      currentStep: steps.PRINT,
    };

    this.stringifyPrinters = '';
    this.isFirstPollingTracked = false;
    this.isAppRunning = false;
    this.printersCount = 0;

    this.close = this.close.bind(this);
    this.setStep = this.setStep.bind(this);
    this.getStep = this.getStep.bind(this);
    this.setPrinter = this.setPrinter.bind(this);

    this.localPrintersPoll();
  }

  /**
   * Change the modal step
   * @param {String} currentStep - One of the available steps
   * @param {Object} stepData - Object of data to pass to the next step
   */
  setStep(currentStep, stepData = {}) {
    if (!steps[currentStep.toUpperCase()]) {
      return;
    }
    this.setState(
      Object.assign(
        {
          currentStep,
        },
        stepData
      )
    );
  }

  /**
   * Get the current modal step a user is viewing
   * @returns {string}
   */
  getStep() {
    return this.state.currentStep;
  }

  /**
   * Set the modal's selected printer
   * @param {Object} printer
   */
  setPrinter(printer) {
    this.setState(
      {
        printer,
      },
      () => {
        this.printerStatusPoll();
      }
    );

    // Allow to send ZPL commands to the selected printer
    window.printer = printer;
  }

  /**
   * When the ZBP modal is opened, the connected printers are polled
   * every X MS to keep the printers list updated
   */
  async localPrintersPoll() {
    let durationStart = null;
    if (!this.isFirstPollingTracked) {
      durationStart = Date.now();
    }

    let printers;
    try {
      printers = await getLocalPrinters();
      if (!this.state.show) return;
      this.isAppRunning = true;

      this.printersCount = printers && Array.isArray(printers) ? printers.length : 0;

      if (
        this.state.currentStep === steps.ERROR &&
        (this.state.error === 'EMPTY_API_RESPONSE' || this.state.error === 'GENERIC')
      ) {
        // If the API gives an error (empty label queue), let's not get further
        return;
      }
      if (this.state.currentStep === steps.INSTALLING) {
        this.setState({
          isInstalled: true,
        });

        setTimeout(() => {
          if (!printers || !Array.isArray(printers)) {
            this.stringifyPrinters = '';
            this.setStep(steps.ERROR, {
              error: 'NO_PRINTER',
              printer: {},
              printers: [],
            });
          } else {
            this.setStep(steps.PRINT);
          }
        }, timeouts.appInstalledRedirect);

        return;
      }

      if (!printers || !Array.isArray(printers)) {
        if (this.state.currentStep !== steps.ERROR) {
          // If there was a printer previously set, remove it (App crash)
          this.stringifyPrinters = '';
          this.setStep(steps.ERROR, {
            error: 'NO_PRINTER',
            printer: {},
            printers: [],
          });
        }
        return;
      }

      if (!this.isFirstPollingTracked) {
        this.isFirstPollingTracked = true;
      }
    } catch (_err) {
      if (!this.state.show) return;
      // Unable to get printers.. App is closed OR SSL is not yet accepted
      if (steps.PRINT === this.state.currentStep || steps.ERROR === this.state.currentStep) {
        this.setStep(steps.INSTALL);
      }

      if (this.isAppRunning) {
        this.isAppRunning = false;
      }

      if (this.stringifyPrinters !== '') {
        this.stringifyPrinters = '';
        this.setState({
          printer: {},
          printers: [],
        });
      }
    }
    const tmpStringifyPrinters = JSON.stringify(printers) || '';
    if (tmpStringifyPrinters !== this.stringifyPrinters) {
      this.stringifyPrinters = tmpStringifyPrinters;

      // Get the printer Host identification only if the printer payload has changed
      // eslint-disable-next-line no-restricted-syntax
      for (const printer of printers) {
        // eslint-disable-next-line no-await-in-loop
        const identification = await getLabelPrinterModel(printer);
        printer.identification = identification;
      }

      if (this.state.currentStep === steps.INSTALLING) {
        this.setState({
          printers,
        });
      } else {
        this.setStep(steps.PRINT, {
          printers,
        });
      }
      this.setPrinter(getDefaultPrinter(printers));
    }

    if (!this.state.show) return;
    // Poll whenever it is a success or a failed XHR
    setTimeout(
      () => {
        this.localPrintersPoll();
      },
      this.printersCount > 0 ? timeouts.getPrinters : timeouts.getPrintersInitial
    );
  }

  /**
   * When the ZBP modal is opened and a list of printers are provided,
   * the default printer is polled to listen for a status change.
   */
  printerStatusPoll() {
    const { printer, currentStep } = this.state;
    const printerUid = printer.uid;

    // Printer might be unset
    if (!Object.keys(printer).length) return;

    getPrinterStatus(printer).then((error) => {
      // Stop polling if the modal is closed
      if (!this.state.show) return;
      // If the selected printer was changed, abort the previous poll
      if (printerUid !== this.state.printer.uid) return;
      if (currentStep === steps.ERROR && this.state.error !== 'GENERIC') return;

      if (error && currentStep !== steps.ERROR) {
        this.setStep(LabelPrintModal.steps.ERROR, {
          error,
        });
      } else if (
        !error &&
        currentStep === steps.ERROR &&
        this.state.error !== 'EMPTY_API_RESPONSE'
      ) {
        this.setStep(LabelPrintModal.steps.PRINT);
      }

      setTimeout(() => {
        this.printerStatusPoll();
      }, timeouts.getPrinterStatus);
    });
  }

  /**
   * Action when closing the modal
   */
  close() {
    this.setState(
      {
        show: false,
      },
      () => {
        // In case the animation end was not triggered
        setTimeout(() => {
          this.props.renderedComponent.unmount();
        }, 250);
      }
    );
  }

  /**
   * Render the Label modal according to the current step
   * and pass the data it requires
   * @returns {Object} DOM Element
   */
  render() {
    const { labelApiUrl } = this.props;
    const modalOptions = Object.assign(
      {
        show: this.state.show,
        onHide: this.close,
        setStep: this.setStep,
        getStep: this.getStep,
        setPrinter: this.setPrinter,
        labelApiUrl,
      },
      this.state
    );

    const componentClass = Components[this.state.currentStep];

    return React.createElement(componentClass, { ...modalOptions });
  }
}

LabelPrintModal.propTypes = {
  labelApiUrl: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  renderedComponent: PropTypes.object.isRequired,
};

export default LabelPrintModal;
